import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { api, formatApiError, BACKEND_URL } from '../lib/api';
import { Logo } from '../components/Logo';
import { Navbar, Footer } from '../components/Navbar';
import { AmbientMusicButton } from '../components/AmbientMusicButton';
import { LocateGravesite } from '../components/LocateGravesite';
import { Heart, Lock, Share2, MapPin, Calendar, PenSquare, X, Check, Play, ChevronLeft, ChevronRight, Headphones, Volume2, Copy, Mic } from 'lucide-react';
import { formatPartialDate } from '../components/PartialDateInput';
import { focalPosition } from '../lib/photoFocal';
import {
  QuickNav, AboutCard, UpcomingServices, TributesSection,
  FuneralHomeCard, CreateMemorialCTA,
} from '../components/PublicMemorialSections';
import { FamilyTreeView } from '../components/FamilyTreeView';
import { VoicePlayer } from '../components/VoicePlayer';
import { registerAudioSource, claimAudioFocus } from '../lib/audioBus';
import { toast } from 'sonner';

const MEDIA_URL = (m) => {
  if (m.external_url) return m.external_url;
  if (m.storage_path && m.media_id) return `${BACKEND_URL}/api/media/${m.media_id}/file`;
  if (m.media_id) return `${BACKEND_URL}/api/media/${m.media_id}/file`;
  return null;
};

export default function PublicMemorial() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [video, setVideo] = useState(null);
  const [showRequest, setShowRequest] = useState(false);  // false | 'private' | 'vault'
  const [accessChoice, setAccessChoice] = useState(null);  // null | 'private' | 'vault'
  const [showLightbox, setShowLightbox] = useState(null);
  const [activeLeaf, setActiveLeaf] = useState(null);
  const [pinAttempt, setPinAttempt] = useState('');
  const [pinError, setPinError] = useState(null);
  const treeRef = useRef(null);
  const [showShare, setShowShare] = useState(false);
  // iter93 — render the canonical QR directly on the page (small badge in
  // hero + larger card before footer) so visitors don't have to open the
  // share modal to scan. The QR encodes the same public URL the modal
  // serves, so engraved plaques and on-page scans resolve identically.
  const [heroQrUrl, setHeroQrUrl] = useState(null);
  const [scanQrUrl, setScanQrUrl] = useState(null);

  const fetchMemorial = (pin) => {
    const url = pin
      ? `/public/memorials/${slug}?pin=${encodeURIComponent(pin)}`
      : `/public/memorials/${slug}`;
    return api.get(url)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(formatApiError(e, 'Memorial not found')));
  };

  useEffect(() => {
    fetchMemorial();
  }, [slug]);

  useEffect(() => {
    if (!data || data.locked) return;
    api.get(`/memorials/${data.memorial.memorial_id}/video`)
      .then(({ data: v }) => setVideo(v))
      .catch(() => {});
  }, [data]);

  // iter97.8 — Previously we smooth-scrolled to the gravesite map on
  // mount whenever the URL ended in `#locate`. Intent was: visitors
  // physically AT the cemetery scan the headstone QR and get directions
  // in one tap. Reality: anyone scanning the same QR from a shared
  // link, a print piece, or elsewhere was dropped at the bottom of the
  // page with no context.
  //
  // New behaviour: detect `#locate` on mount → strip the hash → scroll
  // back to top (the browser auto-scrolls to the anchor as soon as the
  // section renders, so we need to undo that) → surface a floating
  // "Directions to gravesite" CTA. One tap from any visitor scrolls
  // them to the map.
  const [arrivedWithLocateHash] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.location.hash === '#locate';
  });
  const [showLocateCta, setShowLocateCta] = useState(false);

  useEffect(() => {
    if (!arrivedWithLocateHash) return;
    if (typeof window === 'undefined') return;
    // Strip the hash so React Router / browser don't keep trying to
    // jump on subsequent re-renders.
    try {
      const u = new URL(window.location.href);
      u.hash = '';
      window.history.replaceState(window.history.state, '', u.toString());
    } catch (_) { /* noop */ }
  }, [arrivedWithLocateHash]);

  // Once memorial data has loaded and the gravesite section is in the
  // DOM, the browser will have just auto-scrolled to it. Undo that and
  // light up the CTA so the visitor lands at the top of the memorial.
  useEffect(() => {
    if (!arrivedWithLocateHash) return;
    if (!data || data.locked) return;
    const mem = data.memorial || {};
    if (mem.gravesite_lat != null && mem.gravesite_lng != null) {
      // Wait one frame for the section to render, then snap back to
      // the top and reveal the CTA.
      requestAnimationFrame(() => {
        try { window.scrollTo({ top: 0, behavior: 'instant' }); } catch (_) {
          window.scrollTo(0, 0);
        }
        setShowLocateCta(true);
      });
    }
  }, [data, arrivedWithLocateHash]);

  const scrollToLocate = () => {
    const el = document.getElementById('locate');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleVideoEnded = () => {
    // Smooth auto-transition into the Family section (or next content)
    treeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const [videoFading, setVideoFading] = useState(false);
  // iter100 — Audio focus: the Life Journey video is a registered audio
  // source; playing it silences ambient music / narration / voice
  // memories, and playing any of those pauses the video.
  const videoElRef = useRef(null);
  useEffect(() => {
    const unregister = registerAudioSource('life-journey-video', () => {
      const v = videoElRef.current;
      if (v && !v.paused) v.pause();
    });
    return unregister;
  }, []);

  // iter94.7 — Per-memorial billing gate. Unpaid memorials must NOT
  // surface any QR/share affordances on the public page (the QR is the
  // primary feature behind the paywall). The owner can still preview
  // their memorial, but visitors see a "memorial in setup" notice in
  // place of the QR card.
  const isPaid = !data?.memorial || (data.memorial.plan_status ?? 'paid') === 'paid';

  // iter93 — generate the two on-page QR variants whenever the memorial
  // loads. Small dense version for the hero badge (192px, low error
  // correction since it's decorative-sized), larger crisp version for
  // the standalone "Scan to share" card (512px, medium ECC so it scans
  // reliably even from a phone screen).
  useEffect(() => {
    if (!data?.memorial?.slug || !isPaid) {
      setHeroQrUrl(null); setScanQrUrl(null);
      return;
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://myleaflegacy.com';
    const shareUrl = `${origin}/m/${data.memorial.slug}`;
    QRCode.toDataURL(shareUrl, {
      width: 192,
      margin: 1,
      errorCorrectionLevel: 'L',
      color: { dark: '#1A3626', light: '#F7F3E8' },
    }).then(setHeroQrUrl).catch(() => setHeroQrUrl(null));
    QRCode.toDataURL(shareUrl, {
      width: 512,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#1A3626', light: '#F7F3E8' },
    }).then(setScanQrUrl).catch(() => setScanQrUrl(null));
  }, [data?.memorial?.slug, isPaid]);

  if (err) return <NotFound err={err} />;
  if (!data) return <Loading />;

  // PRIVATE QR — present three clear access types so a visitor knows what
  // they're being offered. They can:
  //   1. Public View      — open if the family allows it (toggle off here)
  //   2. Private View     — enter the PIN OR request access from the family
  //   3. Family Vault     — request access to private documents/letters
  // The user picks a path before the PIN form appears.
  // iter95.3 — Public preview paywall. When the memorial hasn't been
  // activated, show a dignified placeholder before the PIN gate.
  if (data.unpaid_locked) {
    const m = data.memorial;
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="unpaid-memorial-gate">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6 py-14">
          <div className="max-w-md w-full text-center">
            {m.profile_photo_url && (
              <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border border-[hsl(var(--accent))]/40 bg-[hsl(var(--muted))] grayscale opacity-90">
                <img src={m.profile_photo_url} alt={m.full_name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="ml-overline mt-6">Memorial</div>
            <h1
              className="font-serif font-light text-3xl sm:text-4xl mt-3 text-[hsl(var(--primary))] tracking-tight"
              data-testid="unpaid-memorial-name"
            >
              {m.full_name}
            </h1>
            {(m.date_of_birth || m.date_of_passing) && (
              <div className="mt-3 text-sm text-[hsl(var(--primary)/0.55)]">
                {m.date_of_birth?.slice(0, 4) || '—'}
                {' — '}
                {m.date_of_passing?.slice(0, 4) || '—'}
              </div>
            )}
            <div className="ml-divider-leaf w-12 mx-auto my-7" aria-hidden />
            <h2 className="font-serif text-xl text-[hsl(var(--primary))]">
              This memorial isn&apos;t activated yet.
            </h2>
            <p className="mt-4 ml-caption leading-relaxed">
              The family is still preparing this page. Photos, stories, and the
              Life Journey will appear here as soon as it&apos;s activated.
              Please check back soon.
            </p>
            <p className="mt-7 text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.45)]">
              Forever Here · MyLeaf Legacy
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ----- PIN-gated memorial (private QR mode) -----
  if (data.locked) {
    const submitPin = async (e) => {
      e.preventDefault();
      setPinError(null);
      const pin = (pinAttempt || '').trim();
      if (pin.length < 4) {
        setPinError('Please enter the PIN provided by the family.');
        return;
      }
      try {
        const { data: fresh } = await api.get(
          `/public/memorials/${slug}?pin=${encodeURIComponent(pin)}`,
        );
        if (fresh?.locked) {
          setPinError('That PIN didn\'t match. Please double-check.');
          return;
        }
        setData(fresh);
      } catch {
        setPinError('We couldn\'t verify that PIN. Please try again.');
      }
    };
    const m = data.memorial;
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="private-memorial-gate">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6 py-14">
          <div className="max-w-xl w-full">
            <div className="text-center">
              {m.profile_photo_url && (
                <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border border-[hsl(var(--accent))]/40 bg-[hsl(var(--muted))]">
                  <img src={m.profile_photo_url} alt={m.full_name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="ml-overline mt-6">Memorial access</div>
              <h1 className="font-serif font-light text-3xl sm:text-4xl mt-3 text-[hsl(var(--primary))] tracking-tight">
                {m.full_name}
              </h1>
              <p className="mt-4 ml-caption leading-relaxed">
                Choose how you would like to view this memorial.
              </p>
            </div>

            {/* Three access cards */}
            <div className="mt-9 space-y-3" data-testid="access-options">
              <AccessOption
                id="public"
                title="Public View"
                description="The open memorial — photos, story, and the Life Journey video."
                state="locked"
                hint="The family has chosen to keep this memorial private. Use Private View below."
                testid="access-option-public"
              />
              <AccessOption
                id="private"
                title="Private View"
                description="Enter the PIN the family shared, or send them a request."
                state={accessChoice === 'private' ? 'open' : 'available'}
                onClick={() => setAccessChoice('private')}
                testid="access-option-private"
              >
                {accessChoice === 'private' && (
                  <>
                    <form onSubmit={submitPin} className="mt-5 space-y-3" data-testid="private-pin-form">
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={pinAttempt}
                        onChange={(e) => setPinAttempt(e.target.value)}
                        placeholder="Enter PIN"
                        maxLength={12}
                        className="ml-input text-center tracking-[0.4em] font-serif text-lg"
                        data-testid="private-pin-input"
                      />
                      {pinError && (
                        <div className="text-sm text-red-700" data-testid="private-pin-error">{pinError}</div>
                      )}
                      <button type="submit" className="ml-btn-primary w-full" data-testid="private-pin-submit">
                        Unlock memorial
                      </button>
                    </form>
                    <div className="mt-4 text-center">
                      <button
                        type="button"
                        onClick={() => setShowRequest('private')}
                        className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))] hover:text-[hsl(var(--primary))]"
                        data-testid="private-request-access-btn"
                      >
                        Don't have a PIN? Request family access
                      </button>
                    </div>
                  </>
                )}
              </AccessOption>
              <AccessOption
                id="vault"
                title="Family Vault"
                description="Letters, will, and private documents. Admin shares the PIN manually."
                state={accessChoice === 'vault' ? 'open' : 'available'}
                onClick={() => { setAccessChoice('vault'); setShowRequest('vault'); }}
                testid="access-option-vault"
              />
            </div>
          </div>
        </div>
        <Footer />
        {showRequest && (
          <AccessRequestModal slug={slug} kind={showRequest} onClose={() => setShowRequest(false)} />
        )}
      </div>
    );
  }

  const { memorial, life_events, family, media, life_story, services = [], tribute_count = 0, recent_tributes = [], voice = [] } = data;
  const dobYr = memorial.date_of_birth?.slice(0, 4) || '';
  const dodYr = memorial.date_of_passing?.slice(0, 4) || '';
  const years = (dobYr || dodYr) ? `${dobYr || '—'} — ${dodYr || '—'}` : '';
  const hasVideo = video && video.status === 'ready' && video.video_url;
  const videoSrc = hasVideo
    ? `${BACKEND_URL}${video.video_url}${video.video_url.includes('?') ? '&' : '?'}v=${video.render_id || video.generated_at || ''}`
    : null;
  const posterSrc = video?.poster_url ? `${BACKEND_URL}${video.poster_url}` : null;

  // Ambient music source — admin's chosen Life Journey track is the
  // single source of truth (persisted to default_music_track). Custom
  // uploads still win because the renderer treats "custom:{id}" as such.
  // Falls back to the legacy ambient_music_track field for older memorials.
  let ambientSrc = null;
  const chosen = memorial.default_music_track || memorial.ambient_music_track || null;
  // iter78 — Cache-bust the custom music URL with the latest upload
  // timestamp so a re-upload of the same memorial always serves the
  // new file (the storage path changes but the public URL doesn't).
  const cmV = encodeURIComponent(memorial.custom_music?.uploaded_at || '');
  if (chosen && chosen.startsWith('custom:') && memorial.custom_music?.storage_path) {
    ambientSrc = `${BACKEND_URL}/api/memorials/${memorial.memorial_id}/music/custom.mp3?v=${cmV}`;
  } else if (memorial.custom_music?.storage_path && !chosen) {
    // Legacy: no choice persisted yet but a custom upload exists — play it.
    ambientSrc = `${BACKEND_URL}/api/memorials/${memorial.memorial_id}/music/custom.mp3?v=${cmV}`;
  } else if (chosen && chosen !== 'silence' && !chosen.startsWith('custom:')) {
    ambientSrc = `${BACKEND_URL}/api/music/${chosen}/preview.mp3`;
  }

  return (
    <div className="min-h-screen bg-background relative">
      <Navbar />
      {ambientSrc && <AmbientMusicButton src={ambientSrc} label="Memorial music" autoPlay />}

      {/* iter97.8 — Floating CTA for QR-scanned-at-headstone visitors.
          Only renders when the URL had `#locate` AND the memorial has a
          gravesite pin. Tapping smooth-scrolls to the map without us
          surprise-scrolling everyone else who lands here. */}
      {showLocateCta && (
        <div
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-30 max-w-xs"
          data-testid="public-locate-cta"
        >
          <button
            type="button"
            onClick={() => { scrollToLocate(); setShowLocateCta(false); }}
            className="flex items-center gap-3 px-4 py-3 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--background))] shadow-lg hover:scale-[1.02] active:scale-95 transition-transform"
            aria-label="Get directions to gravesite"
          >
            <MapPin className="w-4 h-4 shrink-0" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-semibold whitespace-nowrap">
              Directions to gravesite
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowLocateCta(false); }}
              className="ml-1 w-6 h-6 rounded-full flex items-center justify-center text-[hsl(var(--background))]/70 hover:text-[hsl(var(--background))] hover:bg-white/10"
              aria-label="Dismiss"
              data-testid="public-locate-cta-dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </button>
        </div>
      )}
      {/* iter94.9 — Unpaid notice card.
          Replaces the silent "Sharing locked" pill with an explicit
          status banner just under the top navbar, visible on any
          memorial whose plan_status !== 'paid'. Owners (and admins
          who land here while looking at a customer's memorial)
          can tap straight through to billing. */}
      {!isPaid && (
        <section
          className="ml-container py-5"
          data-testid="public-unpaid-banner"
        >
          <div className="max-w-3xl mx-auto rounded-md border border-[hsl(var(--accent)/0.45)] bg-[hsl(var(--accent)/0.08)] px-5 py-4 flex items-center gap-4 flex-wrap">
            <Lock className="w-4 h-4 text-[hsl(var(--accent))] shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary))]">
                Memorial not yet activated
              </div>
              <p className="text-[13px] text-[hsl(var(--primary)/0.78)] mt-1 leading-snug">
                The QR code, sharing, and Family Vault are paused until this memorial is activated. Activation is a one-time setup so the page can be shared publicly.
              </p>
            </div>
            <Link
              to={`/dashboard/m/${memorial.memorial_id}?tab=billing`}
              className="ml-btn-gold inline-flex items-center gap-2"
              data-testid="public-unpaid-activate"
            >
              Activate memorial
            </Link>
          </div>
        </section>
      )}

      <div className="ml-container py-3 flex items-center justify-end">
        {isPaid ? (
          <button
            onClick={() => setShowShare(true)}
            className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.55)] hover:text-[hsl(var(--primary))] transition-colors flex items-center gap-1.5"
            data-testid="share-memorial-btn">
            <Share2 className="w-3.5 h-3.5" /> Share this memorial
          </button>
        ) : (
          <Link
            to={`/dashboard/m/${memorial.memorial_id}?tab=billing`}
            className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[hsl(var(--accent))] hover:underline flex items-center gap-1.5"
            data-testid="share-memorial-activate"
            title="Activate this memorial to unlock QR sharing"
          >
            <Lock className="w-3.5 h-3.5" /> Activate to enable sharing
          </Link>
        )}
      </div>

      {showShare && isPaid && (
        <ShareMemorialModal
          memorial={memorial}
          onClose={() => setShowShare(false)}
        />
      )}

      {/* iter93 — Floating Scan-to-share badge anchored to the top right
          of the hero on tablet+. Hidden on small phones (the dedicated
          card lower on the page covers mobile). Quietly suggests scan or
          tap-to-open. Hidden entirely on unpaid memorials (iter94.7). */}
      {heroQrUrl && isPaid && (
        <button
          type="button"
          onClick={() => setShowShare(true)}
          aria-label="Open share & scan options"
          data-testid="hero-qr-badge"
          className="hidden md:flex absolute right-4 lg:right-8 top-24 lg:top-28 z-10 flex-col items-center gap-1.5 rounded-xl bg-[hsl(var(--background))] border border-[hsl(var(--primary)/0.14)] shadow-[0_18px_40px_-26px_rgba(26,54,38,0.4)] px-3 py-3 hover:shadow-[0_22px_50px_-26px_rgba(26,54,38,0.55)] hover:border-[hsl(var(--accent)/0.55)] transition-all"
        >
          <img
            src={heroQrUrl}
            alt={`Scan to open ${memorial.full_name}'s memorial`}
            className="w-20 h-20 rounded-md"
          />
          <span className="text-[9px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--primary)/0.78)]">
            Scan to open
          </span>
        </button>
      )}

      {/* iter94.9 — Unpaid placeholder for the hero QR badge slot.
          Replaces the previously-empty space with an explicit "QR
          unavailable · Activate" card so the owner sees the path. */}
      {!isPaid && (
        <Link
          to={`/dashboard/m/${memorial.memorial_id}?tab=billing`}
          aria-label="Activate memorial to enable QR"
          data-testid="hero-qr-activate"
          className="hidden md:flex absolute right-4 lg:right-8 top-24 lg:top-28 z-10 flex-col items-center gap-2 rounded-xl bg-[hsl(var(--background))] border border-dashed border-[hsl(var(--accent)/0.55)] shadow-[0_18px_40px_-26px_rgba(26,54,38,0.25)] px-3 py-3 w-24 hover:bg-[hsl(var(--accent)/0.08)] transition-all"
        >
          <div className="w-20 h-20 rounded-md bg-[hsl(var(--accent)/0.08)] border border-dashed border-[hsl(var(--accent)/0.4)] flex flex-col items-center justify-center gap-1">
            <Lock className="w-5 h-5 text-[hsl(var(--accent))]" />
            <span className="text-[8px] uppercase tracking-[0.2em] font-semibold text-[hsl(var(--accent))]">
              QR
            </span>
          </div>
          <span className="text-[9px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--accent))]">
            Activate
          </span>
        </Link>
      )}

      {/* HERO — calm first impression. Portrait · name · dates · location.
          Buttons are intentionally quiet so they don't dominate the entry. */}
      <section className="ml-container pt-12 sm:pt-20 pb-20 text-center">
        <div className="relative inline-block" data-testid="public-hero">
          <div className="absolute -inset-3 rounded-full bg-[hsl(var(--accent))]/10 blur-2xl" />
          <div className="relative w-40 h-40 sm:w-52 sm:h-52 rounded-full overflow-hidden border border-[hsl(var(--primary)/0.12)] mx-auto bg-[hsl(var(--muted))]">
            {memorial.profile_photo_url ? (
              <img src={memorial.profile_photo_url} alt="" className="w-full h-full object-cover" />
            ) : !isPaid ? (
              // iter94.9 — Unpaid + no portrait set: show an explicit
              // "Activate" tile instead of the silent initial-letter
              // fallback. Click takes the owner straight to billing.
              <Link
                to={`/dashboard/m/${memorial.memorial_id}?tab=billing`}
                className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[hsl(var(--accent)/0.08)] border border-dashed border-[hsl(var(--accent)/0.5)] text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent)/0.14)] transition-colors"
                data-testid="hero-portrait-activate"
                aria-label="Activate memorial to upload a portrait"
              >
                <Lock className="w-6 h-6" />
                <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-center px-2 leading-tight">
                  Activate to upload photo
                </span>
              </Link>
            ) : (
              <div className="w-full h-full flex items-center justify-center font-serif font-light text-5xl text-[hsl(var(--primary))]">
                {memorial.full_name[0]}
              </div>
            )}
          </div>
        </div>
        <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[hsl(var(--primary))] mt-10 px-6 tracking-tight" data-testid="memorial-name">
          {memorial.full_name}
        </h1>
        {(() => {
          // One-line emotional descriptor under the name. Owner-written
          // `hero_tagline` wins when set (max 90 chars, enforced in the
          // backend). Otherwise, derive a short tagline from the first
          // sentence of `life_summary`, stripping the subject's name if
          // it opens the sentence.
          const override = (memorial.hero_tagline || '').trim();
          let tagline = '';
          if (override) {
            tagline = override.length > 90
              ? override.slice(0, 90).trim().replace(/[,;:]$/, '') + '…'
              : override;
          } else if (memorial.life_summary) {
            let s = String(memorial.life_summary).trim();
            const firstName = (memorial.full_name || '').trim().split(/\s+/)[0];
            const fullName  = (memorial.full_name || '').trim();
            if (fullName && s.toLowerCase().startsWith(fullName.toLowerCase())) {
              s = s.slice(fullName.length).trim().replace(/^[,:;-]\s*/, '');
              s = s.charAt(0).toUpperCase() + s.slice(1);
            } else if (firstName && s.toLowerCase().startsWith(firstName.toLowerCase())) {
              s = s.slice(firstName.length).trim().replace(/^[,:;-]\s*/, '');
              s = s.charAt(0).toUpperCase() + s.slice(1);
            }
            const firstSentence = (s.match(/^[^.!?]{6,110}[.!?]/) || [s])[0].trim();
            tagline = firstSentence.length > 95
              ? firstSentence.slice(0, 95).trim().replace(/[,;:]$/, '') + '…'
              : firstSentence.replace(/[.!?]+$/, '');
          }
          if (!tagline) return null;
          return (
            <p
              className="font-serif italic text-[15px] sm:text-[17px] text-[hsl(var(--primary)/0.92)] mt-5 max-w-md mx-auto px-6 leading-snug"
              data-testid="hero-tagline"
            >
              {tagline}
            </p>
          );
        })()}
          {(years || memorial.birthplace) && (
            <div className="mt-5 inline-flex items-center gap-3 text-[11px] uppercase font-semibold tracking-[0.28em] text-[hsl(var(--primary)/0.78)]">
              {years && <><Calendar className="w-3.5 h-3.5" /> {years}</>}
              {memorial.birthplace && <><span className="text-[hsl(var(--accent))]">·</span><MapPin className="w-3.5 h-3.5" /> {memorial.birthplace}</>}
            </div>
          )}
        <div className="mt-10 flex flex-wrap justify-center items-center gap-3 sm:gap-4 px-4">
          <button onClick={() => setShowRequest('vault')} className="ml-btn-gold inline-flex items-center gap-2" data-testid="request-access-btn">
            <Lock className="w-3.5 h-3.5" /> Request Family Vault access
          </button>
          <Link to={`/m/${slug}/memory`} className="ml-btn-gold inline-flex items-center gap-2" data-testid="leave-memory-btn">
            <PenSquare className="w-3.5 h-3.5" /> Leave a memory
          </Link>
          {(memorial.gravesite_lat != null && memorial.gravesite_lng != null) && (
            <a
              href="#locate"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById('locate');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-[12px] tracking-[0.18em] uppercase font-semibold bg-[hsl(var(--primary))] text-[#F4E5C2] hover:bg-[hsl(var(--primary)/0.9)] transition-colors"
              data-testid="hero-locate-btn"
            >
              <MapPin className="w-3.5 h-3.5" /> Locate gravesite
            </a>
          )}
        </div>
      </section>

      {/* iter96 — Sticky quick navigation. Only anchors for sections that exist. */}
      <QuickNav
        sections={[
          services.length > 0 && { id: 'services', label: 'Services' },
          (life_story?.text || memorial.life_summary) && { id: 'story', label: 'Story' },
          media.length > 0 && { id: 'photos', label: 'Photos' },
          hasVideo && { id: 'video', label: 'Video' },
          voice.length > 0 && { id: 'voice', label: 'Voice' },
          { id: 'tributes', label: 'Tributes' },
          family && family.length > 0 && { id: 'family', label: 'Family' },
        ].filter(Boolean)}
      />

      {/* iter96 — About summary card under the hero. */}
      <AboutCard memorial={memorial} />

      {/* iter67 — FEATURED VOICE MEMORY: "Hear them in their own voice",
          pinned near the top of the memorial. Family picks one public
          recording to feature from the Voice tab. */}
      {(() => {
        const fv = (voice || []).find((v) => v.is_featured);
        if (!fv) return null;
        return (
          <section className="ml-container pt-12 pb-2" data-testid="public-featured-voice">
            <div className="mx-auto max-w-2xl rounded-2xl border border-[hsl(var(--accent)/0.45)] bg-[hsl(var(--surface))] shadow-[0_24px_60px_-40px_rgba(26,54,38,0.55)] p-6 sm:p-8 text-center">
              <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[hsl(var(--accent))] inline-flex items-center gap-2">
                <Mic className="w-3.5 h-3.5" />
                Hear them in their own voice
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-[hsl(var(--primary))] mt-3 leading-tight">
                {fv.title}
              </h3>
              {(fv.recorded_date || fv.description) && (
                <p className="mt-2 text-sm italic font-serif text-[hsl(var(--primary)/0.7)]">
                  {fv.description}{fv.description && fv.recorded_date ? ' · ' : ''}{fv.recorded_date}
                </p>
              )}
              <div className="mt-5">
                <VoicePlayer
                  src={`${BACKEND_URL}${fv.file_url}`}
                  seed={fv.voice_id}
                  durationHint={fv.duration_seconds}
                  testid="featured-voice-player"
                />
              </div>
              {fv.transcript && <VoiceTranscript transcript={fv.transcript} voiceId={fv.voice_id} />}
            </div>
          </section>
        );
      })()}

      {/* iter96 — Upcoming Services (hidden when no events exist). */}
      <UpcomingServices services={services} slug={slug} />

      {/* LIFE STORY — moved up (iter96): renders right after Upcoming
          Services so visitors read who they were before the photos. */}
      {(life_story?.text || memorial.life_summary) && (
        <section id="story" className="ml-section ml-container scroll-mt-40" data-testid="public-life-story">
          <div className="max-w-[680px] mx-auto text-center">
            <div className="ml-overline">In their own light</div>
            <div className="ml-divider-leaf w-12 mx-auto my-6" aria-hidden />
            {life_story?.audio_url && (
              <div className="mb-7">
                <LifeStoryListen audioUrl={life_story.audio_url} />
              </div>
            )}
            <p
              className="font-serif italic text-[18px] sm:text-[20px] leading-[1.85] text-[hsl(var(--primary))]"
              data-testid="public-life-story-text"
            >
              {life_story?.text || memorial.life_summary}
            </p>
          </div>
        </section>
      )}

      {/* LIFE EVENTS TIMELINE */}
      {life_events.length > 0 && (
        <section className="ml-section ml-container" data-testid="public-life">
          <div className="text-center mb-16">
            <div className="ml-overline">A Life Journey</div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-[hsl(var(--primary))] mt-5 tracking-tight">
              Chapters
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {life_events.slice().sort((a, b) => (a.start_date || '').localeCompare(b.start_date || '')).map((ev) => (
              <div
                key={ev.event_id}
                data-testid={`chapter-card-${ev.event_id}`}
                className="rounded-sm p-7 sm:p-9 transition-all duration-500 border border-[#C5A059]/55 hover:border-[#1A3626]/60 hover:shadow-[0_18px_40px_-30px_rgba(26,54,38,0.55)]"
                style={{ background: 'linear-gradient(140deg, #F4E5C2 0%, #E9D29B 100%)' }}
              >
                <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#1A3626]/75">
                  {ev.category}
                </div>
                <div className="font-serif font-medium text-2xl mt-3 text-[#1A3626] leading-tight">
                  {ev.title}
                </div>
                {ev.description && (
                  <p className="text-sm mt-4 text-[#1A3626]/85 leading-relaxed">
                    {ev.description}
                  </p>
                )}
                <div className="mt-5 text-[10px] tracking-[0.22em] uppercase font-medium text-[#1A3626]/65">
                  {formatPartialDate(ev.start_date)}{ev.end_date ? ` — ${formatPartialDate(ev.end_date)}` : ''}{ev.location ? ` · ${ev.location}` : ''}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PHOTOS / MOMENTS — moved up so the very first scroll is visual.
          performance first. lazy-load + async decode + intrinsic
          dimensions to prevent CLS. Layout breaks the rigid grid by making
          every 5th image span two columns so the composition feels more
          editorial than template. Click opens a swipeable lightbox. */}
      {media.length > 0 && (
        <section id="photos" className="ml-section ml-container scroll-mt-40" data-testid="public-gallery">
          <div className="text-center mb-14">
            <div className="ml-overline">Photos</div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-[hsl(var(--primary))] mt-5 tracking-tight">
              Moments
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-5 sm:gap-x-3 sm:gap-y-7">
            {media.map((m, i) => {
              const url = MEDIA_URL(m);
              if (!url) return null;
              const wide = (i % 5 === 4);
              return (
                <button
                  key={m.media_id}
                  type="button"
                  onClick={() => setShowLightbox({ startIdx: i })}
                  className={`group relative overflow-hidden bg-[hsl(var(--muted))] ${wide ? 'sm:col-span-2' : ''}`}
                  data-testid={`public-gallery-item-${i}`}
                >
                  <img
                    src={url}
                    alt={m.caption || ''}
                    loading="lazy"
                    decoding="async"
                    width={wide ? 800 : 400}
                    height={400}
                    style={{ objectPosition: focalPosition(m) }}
                    className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04] ${wide ? 'aspect-[2/1]' : 'aspect-square'}`}
                  />
                  {m.caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[hsl(var(--primary))]/85 to-transparent text-[#FDFBF7] text-xs font-light p-4 text-left opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {m.caption}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* LIFE JOURNEY VIDEO — moved up to follow Photos. Shown only when
          public_video_enabled. Cream-tinted rounded frame, gentle fade-out
          on end. No auto-scroll — we let the viewer sit with the silence. */}
      {hasVideo && (
        <section id="video" className="ml-section ml-container scroll-mt-40" data-testid="public-video">
          <div className="text-center mb-14">
            <div className="ml-overline">Life Journey</div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-[hsl(var(--primary))] mt-5 tracking-tight">A Moment</h2>
          </div>
          <div
            className="max-w-xl mx-auto overflow-hidden rounded-2xl border border-[hsl(var(--primary)/0.1)] relative"
            style={{
              background: 'hsl(var(--surface))',
              boxShadow: '0 20px 60px -30px rgba(26,54,38,0.35)',
            }}
          >
            <video
              ref={videoElRef}
              src={videoSrc}
              poster={posterSrc || memorial.profile_photo_url}
              controls
              playsInline
              preload="metadata"
              onEnded={() => setVideoFading(true)}
              onPlay={() => { setVideoFading(false); claimAudioFocus('life-journey-video'); }}
              className="w-full aspect-square relative z-0"
              style={{
                transition: 'opacity 1200ms ease',
                opacity: videoFading ? 0 : 1,
                background: 'hsl(var(--surface))',
              }}
              data-testid="public-life-journey-video"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-[1]"
              style={{
                background: 'linear-gradient(180deg, rgba(26,54,38,0) 55%, rgba(26,54,38,0.22) 100%)',
                mixBlendMode: 'multiply',
              }}
            />
          </div>
        </section>
      )}

      {/* iter96 — TRIBUTES: count, recent preview with family replies,
          and the leave-a-tribute CTA. Story + Chapters moved up to render
          before the gallery. */}
      <TributesSection
        slug={slug}
        memorialName={memorial.full_name}
        tributeCount={tribute_count}
        recentTributes={recent_tributes}
      />

      {/* LOCATE GRAVESITE — only renders if cemetery info exists AND backend
          visibility rules allowed the fields through to this payload. */}
      <LocateGravesite memorial={memorial} />

      {/* iter97 — VOICE MEMORIES: real audio of the deceased (not AI).
          Only public-visibility recordings flow through here. */}
      {voice && voice.length > 0 && (
        <section
          id="voice"
          className="ml-section ml-container scroll-mt-40"
          data-testid="public-voice"
        >
          <div className="text-center mb-10">
            <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[hsl(var(--primary)/0.55)]">
              Voice memories
            </div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-[hsl(var(--primary))] mt-4 tracking-tight inline-flex items-center gap-3">
              <Mic className="w-6 h-6 text-[hsl(var(--accent))]" />
              Hear {memorial.full_name?.split(' ')[0] || 'them'}
            </h2>
            <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-3 max-w-xl mx-auto">
              Real recordings the family has chosen to share.
            </p>
          </div>
          <div className="max-w-2xl mx-auto space-y-4">
            {voice.map((v) => (
              <article
                key={v.voice_id}
                className="border border-[hsl(var(--primary)/0.12)] rounded-2xl p-5 sm:p-6 bg-[hsl(var(--surface))]"
                data-testid={`public-voice-card-${v.voice_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[hsl(var(--background))] border border-[hsl(var(--accent)/0.35)] flex items-center justify-center text-[hsl(var(--accent))] shrink-0">
                    <Mic className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg text-[hsl(var(--primary))] leading-tight">
                      {v.title}
                      {v.is_featured && (
                        <span className="ml-2 align-middle text-[9px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))] border border-[hsl(var(--accent)/0.45)] rounded-full px-2 py-0.5">
                          Featured
                        </span>
                      )}
                    </h3>
                    {v.recorded_date && (
                      <div className="mt-1 text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.55)]">
                        <span>Recorded {v.recorded_date}</span>
                      </div>
                    )}
                    {v.description && (
                      <p className="mt-2 text-sm italic font-serif text-[hsl(var(--primary)/0.78)] leading-relaxed">
                        {v.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <VoicePlayer
                    src={`${BACKEND_URL}${v.file_url}`}
                    seed={v.voice_id}
                    durationHint={v.duration_seconds}
                    testid={`public-voice-player-${v.voice_id}`}
                  />
                </div>
                {v.transcript && <VoiceTranscript transcript={v.transcript} voiceId={v.voice_id} />}
              </article>
            ))}
          </div>
        </section>
      )}

      {/* FAMILY / LOVED ONES — auto-built tree with green (living) /
          gold (in memory) leaves. Falls back to a clean list when there
          aren't enough recognised relationships to form a tree. The
          deceased anchors the trunk. */}
      {family && family.length > 0 && (
        <section
          id="family"
          ref={treeRef}
          className="ml-section py-20 sm:py-24 scroll-mt-40"
          data-testid="public-family"
          style={{ backgroundColor: '#1A3626' }}
        >
          <div className="ml-container">
            <div className="text-center mb-14">
              <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#D9B775]">
                Family
              </div>
              <h2 className="font-serif font-light text-3xl sm:text-4xl text-[#F4E5C2] mt-5 tracking-tight">
                Loved Ones
              </h2>
            </div>
            <div className="max-w-4xl mx-auto px-2 overflow-hidden">
              <FamilyTreeView
                deceasedName={memorial.full_name}
                deceasedPhotoUrl={
                  memorial.profile_photo_url
                    ? (memorial.profile_photo_url.startsWith('http')
                        ? memorial.profile_photo_url
                        : `${BACKEND_URL}${memorial.profile_photo_url}`)
                    : null
                }
                deceasedBirthYear={memorial.date_of_birth ? memorial.date_of_birth.slice(0, 4) : null}
                deceasedDeathYear={memorial.date_of_passing ? memorial.date_of_passing.slice(0, 4) : null}
                members={family}
                onMemberClick={(f) => setActiveLeaf({ type: 'family', family: f })}
                theme="dark"
              />
            </div>
          </div>
        </section>
      )}

      {/* iter96 — Funeral home partnership card ("Services entrusted to"). */}
      <FuneralHomeCard memorial={memorial} />

      {/* GALLERY moved up to render directly after the hero. See top of return. */}


      {/* iter93 — Scan-to-share card. A larger, on-page version of the
          QR so visitors who want to forward the memorial to family in
          another room (or print it) can do so without opening the modal.
          Visible on every screen size; matches the elegant green-on-cream
          brand palette so it feels like part of the memorial rather than
          a tacked-on tool. Hidden on unpaid memorials (iter94.7). */}
      {scanQrUrl && isPaid && (
        <section className="ml-container pt-12 pb-2" data-testid="public-scan-share-card">
          <div
            className="mx-auto max-w-3xl rounded-2xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--background))] shadow-[0_24px_60px_-40px_rgba(26,54,38,0.55)] overflow-hidden grid grid-cols-1 sm:grid-cols-[260px_1fr]"
          >
            <div className="bg-[hsl(var(--surface))] flex items-center justify-center p-7 sm:p-8 border-b sm:border-b-0 sm:border-r border-[hsl(var(--primary)/0.08)]">
              <img
                src={scanQrUrl}
                alt={`QR code to ${memorial.full_name}'s memorial`}
                className="w-44 h-44 sm:w-48 sm:h-48 rounded-md"
                data-testid="scan-share-qr-image"
              />
            </div>
            <div className="p-7 sm:p-9 flex flex-col justify-center">
              <div className="ml-overline">Carry this memorial with you</div>
              <h3 className="font-serif font-light text-2xl sm:text-3xl text-[hsl(var(--primary))] mt-3 leading-snug">
                Scan to open on any phone
              </h3>
              <p className="text-sm text-[hsl(var(--primary)/0.78)] mt-4 leading-relaxed">
                Point any phone camera at the code to open {memorial.full_name}'s memorial. This is the same QR engraved on aluminum plaques — share it with family who could not be here today.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setShowShare(true)}
                  className="ml-btn-gold inline-flex items-center gap-2"
                  data-testid="scan-share-open-modal"
                >
                  <Share2 className="w-3.5 h-3.5" /> Share & copy link
                </button>
                <a
                  href={scanQrUrl}
                  download={`myleaf-${memorial.slug}-qr.png`}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-[11px] tracking-[0.18em] uppercase font-semibold border border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary))] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors"
                  data-testid="scan-share-download-qr"
                >
                  Download QR
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* iter94.9 — Unpaid placeholder for the scan-to-share card.
          Same shape as the paid scan card so the page rhythm is
          preserved; CTA goes straight to billing. */}
      {!isPaid && (
        <section className="ml-container pt-12 pb-2" data-testid="public-scan-share-unpaid">
          <div className="mx-auto max-w-3xl rounded-2xl border border-dashed border-[hsl(var(--accent)/0.55)] bg-[hsl(var(--background))] overflow-hidden grid grid-cols-1 sm:grid-cols-[260px_1fr]">
            <div className="bg-[hsl(var(--surface))] flex items-center justify-center p-7 sm:p-8 border-b sm:border-b-0 sm:border-r border-[hsl(var(--primary)/0.08)]">
              <div className="w-44 h-44 sm:w-48 sm:h-48 rounded-md bg-[hsl(var(--accent)/0.06)] border border-dashed border-[hsl(var(--accent)/0.45)] flex flex-col items-center justify-center gap-2">
                <Lock className="w-8 h-8 text-[hsl(var(--accent))]" />
                <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))]">
                  QR unavailable
                </span>
              </div>
            </div>
            <div className="p-7 sm:p-9 flex flex-col justify-center">
              <div className="ml-overline text-[hsl(var(--accent))]">Activate memorial</div>
              <h3 className="font-serif font-light text-2xl sm:text-3xl text-[hsl(var(--primary))] mt-3 leading-snug">
                QR sharing unlocks after activation
              </h3>
              <p className="text-sm text-[hsl(var(--primary)/0.78)] mt-4 leading-relaxed">
                Once this memorial is activated, families can scan the QR code from a plaque, share it on any phone, and visitors can leave memories in the Family Vault. Activation is a one-time setup.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/dashboard/m/${memorial.memorial_id}?tab=billing`}
                  className="ml-btn-gold inline-flex items-center gap-2"
                  data-testid="scan-share-activate-btn"
                >
                  <Lock className="w-3.5 h-3.5" /> Activate memorial
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-[11px] tracking-[0.18em] uppercase font-semibold border border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary))] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors"
                  data-testid="scan-share-pricing-btn"
                >
                  View plans
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* iter96 — Preserve Your Family Story CTA. */}
      <CreateMemorialCTA />

      {/* MEMORIAL ATTRIBUTION — kept narrow and quiet, sits above the
          shared dark-green Footer that closes every page on the site. */}
      <div className="ml-container pt-16 pb-12 mt-12 border-t border-[hsl(var(--primary)/0.08)] text-center">
        <Logo size={36} className="justify-center" />
        <p className="ml-caption mt-6">
          This memorial is kept by the family of {memorial.full_name}.
        </p>
        <div className="ml-overline mt-4">Forever Here</div>
      </div>

      <Footer />

      {showRequest && <AccessRequestModal slug={slug} kind={showRequest === 'private' ? 'private' : 'vault'} onClose={() => setShowRequest(false)} />}
      {showLightbox && (
        <Lightbox
          items={media.map((m) => ({ url: MEDIA_URL(m), caption: m.caption })).filter((x) => x.url)}
          startIdx={showLightbox.startIdx || 0}
          onClose={() => setShowLightbox(null)}
        />
      )}
      {activeLeaf && <LeafModal leaf={activeLeaf} memorial={memorial} media={media} onClose={() => setActiveLeaf(null)} />}
    </div>
  );
}


function LeafModal({ leaf, memorial, media, onClose }) {
  const person = leaf.type === 'root' ? {
    name: memorial.full_name,
    photo_url: memorial.profile_photo_url,
    relationship: 'In memory',
    birth_date: memorial.date_of_birth,
    death_date: memorial.date_of_passing,
    location: memorial.birthplace,
    notes: memorial.life_summary,
    is_living: false,
  } : leaf.family;

  // Swipeable photos: for root, show all public media; for family members, show their leaf photo + a subset
  const photos = leaf.type === 'root'
    ? media.map((m) => ({ url: MEDIA_URL(m), caption: m.caption })).filter((x) => x.url)
    : (person.photo_url ? [{ url: person.photo_url, caption: null }] : []);

  const [idx, setIdx] = useState(0);
  const hasPhotos = photos.length > 0;
  const go = (d) => setIdx((i) => (i + d + photos.length) % photos.length);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-background w-full max-w-xl sm:rounded-2xl overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="family-member-panel">
        <div className="relative aspect-square bg-[hsl(var(--muted))]">
          {hasPhotos ? (
            <>
              <img src={photos[idx].url} alt="" className="w-full h-full object-contain" />
              {photos.length > 1 && (
                <>
                  <button onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-2" data-testid="leaf-prev"><ChevronLeft className="w-5 h-5" /></button>
                  <button onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-2" data-testid="leaf-next"><ChevronRight className="w-5 h-5" /></button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {photos.map((_, i) => (
                      <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/40'}`} />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center font-serif text-5xl text-[hsl(var(--primary))]">
              {person.name[0]}
            </div>
          )}
          <button onClick={onClose} className="absolute top-3 right-3 bg-black/50 text-white rounded-full p-2" data-testid="family-member-panel-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 overflow-auto">
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${person.is_living ? 'bg-[hsl(var(--secondary))]' : 'bg-[hsl(var(--accent))]'}`} />
            <span className="ml-overline">{person.is_living ? 'With us today' : 'In memory'}</span>
          </div>
          <h3 className="font-serif text-3xl text-[hsl(var(--primary))] mt-1">{person.name}</h3>
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            {person.relationship}
            {(person.birth_date || person.death_date) && (
              <> · {person.birth_date || '—'} — {person.death_date || (person.is_living ? 'present' : '—')}</>
            )}
            {person.location && <> · {person.location}</>}
          </div>
          {person.notes && <p className="mt-4 text-[hsl(var(--foreground))]/85 leading-relaxed">{person.notes}</p>}
          {/* iter97.10 — Linked memorial deep-link. Backend strips
              linked_memorial_slug from public payload unless the linked
              memorial is paid + published, so this CTA is privacy-safe. */}
          {person.linked_memorial_slug && (
            <a
              href={`/m/${person.linked_memorial_slug}`}
              className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--background))] text-[11px] tracking-[0.22em] uppercase font-semibold hover:opacity-90"
              data-testid="family-member-panel-view-memorial"
            >
              View memorial →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Lightbox({ items, startIdx = 0, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  const touchX = useRef(null);

  const go = (d) => {
    if (!items.length) return;
    setIdx((i) => (i + d + items.length) % items.length);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx > 0 ? -1 : 1);
    touchX.current = null;
  };

  if (!items.length) return null;
  const current = items[idx];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="gallery-lightbox"
      role="dialog"
      aria-modal="true"
    >
      <button
        className="absolute top-5 right-5 text-white/80 hover:text-white transition-colors p-2"
        onClick={onClose}
        aria-label="Close"
        data-testid="gallery-lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>

      {items.length > 1 && (
        <>
          <button
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-2 transition-colors"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Previous"
            data-testid="gallery-lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-2 transition-colors"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Next"
            data-testid="gallery-lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <figure
        className="max-w-5xl w-full flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={current.url}
          alt={current.caption || ''}
          className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
          data-testid="gallery-lightbox-img"
        />
        {current.caption && (
          <figcaption
            className="font-serif italic text-[15px] text-white/85 text-center max-w-xl px-6"
            data-testid="gallery-lightbox-caption"
          >
            {current.caption}
          </figcaption>
        )}
        {items.length > 1 && (
          <div className="flex items-center gap-1.5 mt-1">
            {items.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${i === idx ? 'w-6 bg-white' : 'w-1 bg-white/40'}`}
              />
            ))}
          </div>
        )}
      </figure>
    </div>
  );
}

// AccessOption — one of three cards on the QR-scan gate. Visually distinct
// for available / open / locked states; renders any nested children only
// when the user actively picks it (open state). Keeps the gate calm and
// premium — no flashing, no big colors, just clean type and a soft border.
function AccessOption({ id, title, description, state, hint, onClick, testid, children }) {
  const isOpen = state === 'open';
  const isLocked = state === 'locked';
  const ordinal = { public: '1', private: '2', vault: '3' }[id] || '';
  return (
    <div
      data-testid={testid}
      className={[
        'relative rounded-lg border px-5 py-4 transition-all',
        isOpen
          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/8 shadow-[0_8px_22px_-12px_rgba(197,160,89,0.35)]'
          : isLocked
            ? 'border-[hsl(var(--primary)/0.1)] bg-[hsl(var(--muted))]/40 opacity-70'
            : 'border-[hsl(var(--primary)/0.15)] bg-[hsl(var(--surface))] hover:border-[hsl(var(--primary)/0.4)] cursor-pointer',
      ].join(' ')}
      onClick={!isLocked && !isOpen ? onClick : undefined}
      role={!isLocked ? 'button' : undefined}
      tabIndex={!isLocked && !isOpen ? 0 : undefined}
      onKeyDown={(e) => {
        if (!isLocked && !isOpen && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick && onClick();
        }
      }}
    >
      <div className="flex items-baseline gap-3">
        <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.5)] w-4 text-right">
          {ordinal}
        </span>
        <div className="flex-1">
          <div className="font-serif text-xl text-[hsl(var(--primary))] leading-tight">
            {title}
          </div>
          <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-1.5 leading-relaxed">
            {description}
          </p>
          {isLocked && hint && (
            <div className="mt-2 text-[11px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--primary)/0.55)]">
              {hint}
            </div>
          )}
          {children}
        </div>
        {!isLocked && !isOpen && (
          <span className="text-[hsl(var(--accent))] text-xl leading-none" aria-hidden>›</span>
        )}
      </div>
    </div>
  );
}



function AccessRequestModal({ slug, onClose, kind = 'vault' }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', relationship: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setStatus('submitting'); setErr(null);
    try {
      await api.post(`/public/memorials/${slug}/access-requests`, form);
      setStatus('done');
    } catch (e2) { setErr(formatApiError(e2)); setStatus('idle'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-background border border-border rounded-2xl w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()} data-testid="access-request-modal">
        <div className="flex justify-between items-start">
          <div>
            <div className="ml-overline">Request access</div>
            <h3 className="font-serif text-2xl mt-2 text-[hsl(var(--primary))]">
              {kind === 'private' ? 'Private memorial' : 'Family Vault'}
            </h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              {kind === 'private'
                ? 'The family will review your request and may share the memorial PIN with you.'
                : 'The Family Vault holds private documents (will, letters). The family decides who can open it.'}
            </p>
          </div>
          <button onClick={onClose} className="text-[hsl(var(--muted-foreground))]"><X /></button>
        </div>
        {status === 'done' ? (
          <div className="py-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[hsl(var(--accent))]/15 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-[hsl(var(--accent))]" />
            </div>
            <h4 className="font-serif text-2xl mt-4 text-[hsl(var(--primary))]">Request sent</h4>
            <p className="text-sm mt-2 text-[hsl(var(--muted-foreground))]">
              An admin will review and respond. If approved, you'll receive a secure link that expires in 24 hours.
            </p>
            <button onClick={onClose} className="ml-btn-outline mt-5">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input required placeholder="Your full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ml-input" data-testid="ar-name" />
            <div className="grid grid-cols-2 gap-3">
              <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ml-input" data-testid="ar-email" />
              <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ml-input" data-testid="ar-phone" />
            </div>
            <input required placeholder="Your relationship to the family" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} className="ml-input" data-testid="ar-rel" />
            <textarea rows={3} placeholder="A short message for the family (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="ml-input" data-testid="ar-msg" />
            {err && <div className="text-sm text-[hsl(var(--destructive))]">{err}</div>}
            <button disabled={status === 'submitting'} className="ml-btn-primary w-full" data-testid="ar-submit">
              {status === 'submitting' ? 'Sending…' : 'Send request'}
            </button>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] text-center">
              By requesting, you agree access may be approved, denied, or revoked by the family admins at any time.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center text-center">
        <div>
          <Logo />
          <div className="mt-6 font-serif text-xl text-[hsl(var(--primary))]">Opening their memorial…</div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

// Minimal, calm audio tribute player. Collapsed by default — a single
// "Listen" pill. On click, expands into the native <audio controls>.
// NEVER auto-plays. Honours prefers-reduced-motion implicitly (no animation
// on the expanded player).
function LifeStoryListen({ audioUrl }) {
  const [open, setOpen] = useState(false);
  const audioRef = useRef(null);
  const src = `${BACKEND_URL}${audioUrl}`;

  // iter100 — Audio focus registration: narration pauses (and is paused
  // by) every other memorial audio source.
  useEffect(() => {
    if (!open) return;
    const unregister = registerAudioSource('life-story-narration', () => {
      const el = audioRef.current;
      if (el && !el.paused) el.pause();
    });
    return unregister;
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    // Wait for the audio element to render, then start playing on the SAME
    // user gesture so mobile browsers allow playback.
    setTimeout(() => {
      const el = audioRef.current;
      if (el) {
        const p = el.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    }, 30);
  };

  if (!open) {
    return (
      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleOpen}
          className="group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[hsl(var(--accent))] text-white text-sm font-semibold tracking-[0.04em] shadow-[0_14px_32px_-18px_rgba(197,160,89,0.7)] hover:bg-[hsl(var(--accent)/0.92)] hover:shadow-[0_18px_40px_-18px_rgba(197,160,89,0.85)] transition-all"
          data-testid="public-life-story-listen-btn"
          aria-label="Listen to a read-aloud of this Life Story"
        >
          <span className="w-7 h-7 rounded-full bg-white/22 flex items-center justify-center group-hover:bg-white/32 transition-colors">
            <Volume2 className="w-4 h-4" strokeWidth={2.2} />
          </span>
          <span>Listen to this story</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="max-w-md mx-auto"
      data-testid="public-life-story-audio"
    >
      <audio
        ref={audioRef}
        src={src}
        controls
        preload="metadata"
        onPlay={() => claimAudioFocus('life-story-narration')}
        className="w-full"
        data-testid="public-life-story-audio-player"
      >
        Your browser does not support audio playback.
      </audio>
      <p className="mt-3 text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.45)] inline-flex items-center justify-center gap-1.5">
        <Volume2 className="w-3 h-3" /> A read-aloud tribute
      </p>
    </div>
  );
}

function NotFound({ err }) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center text-center">
        <div>
          <Logo size={56} />
          <h1 className="font-serif text-4xl mt-6 text-[hsl(var(--primary))]">Memorial not found</h1>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">{err}</p>
          <Link to="/" className="ml-btn-outline mt-6 inline-flex">Return home</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

/**
 * Public Share modal — shows a real, scannable QR for this memorial alongside
 * the canonical URL and standard share affordances. For demo memorials this
 * is the visitor's first proof that "yes, every MyLeaf memorial comes with a
 * scannable QR".
 *
 * QR is generated client-side via the `qrcode` library (same dep already used
 * for Family Referral QR), so it works for anonymous visitors without any
 * admin gating on the backend.
 */
function ShareMemorialModal({ memorial, onClose }) {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://myleaflegacy.com'}/m/${memorial.slug}`;
  const ogShareUrl = `${BACKEND_URL}/api/m/${memorial.slug}/share`;

  useEffect(() => {
    QRCode.toDataURL(shareUrl, {
      errorCorrectionLevel: 'H',
      width: 480,
      margin: 1,
      color: { dark: '#1A3626', light: '#FDFBF7' },
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [shareUrl]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied');
    } catch { toast.error('Copy failed — select and copy manually'); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: memorial.full_name, url: ogShareUrl });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  const download = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `myleaf-${memorial.slug}-qr.png`;
    a.click();
  };

  return (
    <div
      className="fixed inset-0 bg-black/55 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-memorial-modal"
    >
      <div
        className="bg-[hsl(var(--card))] rounded-2xl max-w-md w-full p-7 border border-[hsl(var(--primary)/0.18)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] font-semibold">Share memorial</p>
            <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">{memorial.full_name}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[hsl(var(--primary)/0.6)] hover:text-[hsl(var(--primary))]"
            data-testid="share-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="bg-[hsl(var(--background))] rounded-xl p-5 flex justify-center" data-testid="share-modal-qr-wrap">
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt={`QR code to ${memorial.full_name}'s memorial`}
              className="w-56 h-56"
              data-testid="share-modal-qr-image"
            />
          ) : (
            <div className="w-56 h-56 flex items-center justify-center text-sm text-[hsl(var(--primary)/0.5)]">
              Generating QR…
            </div>
          )}
        </div>
        <p className="text-center text-xs text-[hsl(var(--primary)/0.65)] mt-3 max-w-xs mx-auto">
          Scan with any phone camera to open the memorial. Engraved aluminum plaques use this same QR.
        </p>

        <div className="mt-5 rounded-xl border border-[hsl(var(--primary)/0.15)] bg-[hsl(var(--primary)/0.03)] flex items-center gap-2 p-2">
          <div className="flex-1 truncate text-xs font-mono text-[hsl(var(--primary))] pl-2" data-testid="share-modal-url">
            {shareUrl}
          </div>
          <button
            type="button"
            onClick={copy}
            className="text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))] transition p-2"
            title="Copy link"
            data-testid="share-modal-copy"
          >
            <Copy size={16} />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={download}
            className="ml-btn-outline text-xs"
            disabled={!qrDataUrl}
            data-testid="share-modal-download-qr"
          >
            Download QR
          </button>
          <button
            type="button"
            onClick={nativeShare}
            className="ml-btn-primary text-xs"
            data-testid="share-modal-native-share"
          >
            <Share2 size={14} /> Share…
          </button>
        </div>
      </div>
    </div>
  );
}

/* iter67 — Collapsible transcript under a voice recording. */
function VoiceTranscript({ transcript, voiceId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 text-left">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.6)] hover:text-[hsl(var(--primary))] underline underline-offset-4"
        data-testid={`voice-transcript-toggle-${voiceId}`}
      >
        {open ? 'Hide transcript' : 'Read transcript'}
      </button>
      {open && (
        <p
          className="mt-2 text-sm leading-relaxed font-serif italic text-[hsl(var(--primary)/0.8)] whitespace-pre-wrap border-l-2 border-[hsl(var(--accent)/0.4)] pl-3"
          data-testid={`voice-transcript-${voiceId}`}
        >
          {transcript}
        </p>
      )}
    </div>
  );
}

